import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable, Subscription } from 'rxjs';
import {
  UserClientService,
  TaxOfficeClientService,
  SubscriptionClientService,
  UserResponseDto,
  TaxOfficeResponseDto,
  SubscriptionResponseDto,
  UserRole,
  UpdateCurrentUserRequestDto
} from '../core/api';
import { Jwt } from '../utils/jwt.utils';
import { ConfigService } from './config.service';
import { LocalStorageService } from './local-storage.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {
  private readonly _userClientService = inject(UserClientService);
  private readonly _taxOfficeClientService = inject(TaxOfficeClientService);
  private readonly _subscriptionClientService = inject(SubscriptionClientService);
  private readonly _storageService = inject(LocalStorageService);
  private readonly _authService = inject(AuthService);
  private readonly _subscription = new Subscription();

  private readonly _currentUser$ = new BehaviorSubject<UserResponseDto | null>(null);
  private readonly _taxOfficeData$ = new BehaviorSubject<TaxOfficeResponseDto | null>(null);
  private readonly _subscriptionData$ = new BehaviorSubject<SubscriptionResponseDto | null>(null);
  private _hasLoadedSubscription = false;

  public readonly currentUser$ = this._currentUser$.asObservable();
  public readonly taxOfficeData$ = this._taxOfficeData$.asObservable();
  public readonly subscriptionData$ = this._subscriptionData$.asObservable();

  constructor() {
    // Subscribe to authentication events
    this._subscription.add(
      this._authService.userAuthenticated$.subscribe((user) => {
        this.setCurrentUser(user);
      })
    );

    this._subscription.add(
      this._authService.userLoggedOut$.subscribe(() => {
        this.clearAll();
      })
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public get fullUserDisplayName$(): Observable<string> {
    return this._currentUser$.pipe(map((user) => (user ? `${user.firstName} ${user.lastName}` : '-')));
  }

  public get companyName$(): Observable<string> {
    return this._currentUser$.pipe(map((user) => user?.taxOfficeName || '-'));
  }

  /**
   * @returns The current user value
   */
  public get currentUser(): UserResponseDto | null {
    return this._currentUser$.getValue();
  }

  public async loadCurrentUser(): Promise<void> {
    try {
      const user = await firstValueFrom(this._userClientService.getCurrentUser());
      this._currentUser$.next(user);
    } catch (error) {
      console.error('Failed to load current user:', error);
      this._currentUser$.next(null);
    }
  }

  public setCurrentUser(user: UserResponseDto | null): void {
    this._currentUser$.next(user);
  }

  public async updateCurrentUser(updatedUser: UpdateCurrentUserRequestDto): Promise<UserResponseDto | null> {
    try {
      const user = await firstValueFrom(this._userClientService.updateCurrentUser(updatedUser));
      this._currentUser$.next(user);

      return user;
    } catch (error) {
      console.error('Failed to update current user:', error);
      // Re-throw the error so callers can handle it appropriately
      throw error;
    }
  }

  public async loadTaxOfficeData(): Promise<void> {
    try {
      const accessToken = this._storageService.get('accessToken');
      const taxOfficeId = accessToken ? Jwt.getTaxOfficeId(accessToken) : null;

      if (!taxOfficeId) {
        console.warn('No tax office ID found in token');
        return;
      }

      const taxOfficeData = await firstValueFrom(this._taxOfficeClientService.getTaxOffice(taxOfficeId));
      this._taxOfficeData$.next(taxOfficeData);
    } catch (error) {
      console.error('Failed to load tax office data:', error);
      this._taxOfficeData$.next(null);
    }
  }

  /**
   * Loads the tax office subscription. Never throws — 404/204 means "no subscription", any other failure is
   * logged and leaves the cached value as null.
   *
   * Deliberately non-throwing: this has many callers (app shell, template-editor guard, tax-assessment form,
   * notification settings) that use it for feature toggles and would break on an unhandled rejection. Letting
   * it throw was tried and reverted — it aborted `ngOnInit`/`ngAfterViewInit` in those callers.
   *
   * Swallowing the error does not weaken the subscription lock: the route guard consults this only as its
   * third and last source, behind the database-fresh `/User/me` flag and the token claim, and the backend
   * enforces independently and answers 402 regardless of what the frontend concluded.
   *
   * @param forceRefresh Bypasses the per-session cache. Required wherever a lapse must be noticed mid-session.
   */
  public async loadSubscriptionData(forceRefresh = false): Promise<void> {
    if (this._hasLoadedSubscription && !forceRefresh) {
      return;
    }

    try {
      const subscription = await firstValueFrom(this._subscriptionClientService.getSubscription());
      this._subscriptionData$.next(subscription);
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status !== 404 && status !== 204) {
        console.error('Failed to load subscription data:', error);
      }
      this._subscriptionData$.next(null);
    } finally {
      // Set in every outcome, including failure: leaving it false made each later cached-path call retry the
      // failing request instead of using the cache.
      this._hasLoadedSubscription = true;
    }
  }

  public async initialize(): Promise<void> {
    await Promise.all([this.loadCurrentUser(), this.loadTaxOfficeData(), this.loadSubscriptionData()]);
  }

  public getLogoApiUrl(): string | null {
    const taxOfficeData = this._taxOfficeData$.value;
    if (!taxOfficeData || !taxOfficeData.logoUrl) {
      return null;
    }
    const baseUrl = ConfigService.getConfig().api;
    return `${baseUrl}/api/v1/TaxOffice/${taxOfficeData.id}/Logo?t=${Date.now()}`;
  }

  public clearCurrentUser(): void {
    this._currentUser$.next(null);
  }

  public clearTaxOfficeData(): void {
    this._taxOfficeData$.next(null);
  }

  public clearSubscriptionData(): void {
    this._subscriptionData$.next(null);
    this._hasLoadedSubscription = false;
  }

  public async refreshUserData(): Promise<void> {
    await this.loadCurrentUser();
  }

  public async refreshTaxOfficeData(): Promise<void> {
    await this.loadTaxOfficeData();
  }

  public async refreshSubscriptionData(): Promise<void> {
    await this.loadSubscriptionData(true);
  }

  public getCurrentUser(): UserResponseDto | null {
    return this._currentUser$.value;
  }

  public getCurrentSubscription(): SubscriptionResponseDto | null {
    return this._subscriptionData$.value;
  }

  public isTemplateEditorAllowed(): boolean {
    const subscription = this._subscriptionData$.value;
    return subscription?.isTemplateEditorAllowed ?? subscription?.templateEditorEnabled ?? false;
  }

  public clearAll(): void {
    this.clearCurrentUser();
    this.clearTaxOfficeData();
    this.clearSubscriptionData();
  }

  /**
   * Checks if the current user has any role, which are in the role array
   * Uses JWT utility for token handling
   */
  public hasRole(roles: UserRole[]): boolean {
    const accessToken = this._storageService.get('accessToken');
    if (!accessToken) return false;

    const userRoles = Jwt.getUserRoles(accessToken);
    return roles.some((role) => userRoles.includes(role));
  }
}
